import { useMemo } from 'react';

import type { BubblePlan } from '@innovamat/glow-api-client';
import { useBubblePlansQuery } from '@innovamat/glow-api-client';
import { sortBy } from '@innovamat/radiance-utils';

import { useOrganization } from './use-organization';

type Props = {
  courseOrder?: number;
  enabled?: boolean;
};

type Option = {
  value: string;
  label: string;
};

type UseBubblePlanReturn = {
  bubblePlanOptions: Option[];
  bubblePlans: BubblePlan[];
  loading: boolean;
  getBubblePlanByCourse: (courseId: string) => BubblePlan | null;
};

function useBubblePlans({
  courseOrder,
  enabled = true,
}: Props): UseBubblePlanReturn {
  const { organization } = useOrganization();

  const { data, isLoading } = useBubblePlansQuery(
    {
      regionCode: organization?.operativeRegion!,
      courseOrder: courseOrder,
    },
    {
      enabled: !!organization && enabled,
    }
  );

  const bubblePlans = useMemo(
    () => data?.bubblePlans?.concat().sort(sortBy('courseOrder')) || [],
    [data]
  );

  const bubblePlanOptions: Option[] = useMemo(() => {
    if (!bubblePlans?.length) return [];

    return bubblePlans
      .filter((plan): plan is BubblePlan =>
        Boolean(plan?.id && plan?.courseName)
      )
      .map((plan) => ({
        label: plan.courseName!,
        value: plan.id,
      }));
  }, [bubblePlans]);

  const getBubblePlanByCourse = (courseId: string): BubblePlan | null => {
    return (
      bubblePlans.find((ed) => ed?.courseId === courseId) || bubblePlans[0]
    );
  };

  return {
    bubblePlanOptions,
    bubblePlans,
    loading: isLoading,
    getBubblePlanByCourse,
  };
}

export { useBubblePlans };
